<script lang="ts">
	import { browser } from '$app/environment';
	import { replaceState } from '$app/navigation';
	import { page } from '$app/state';
	import { swipeClose } from '$lib/attachments/swipeClose';
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import type { MapMarkerPoint } from '$lib/components/ui/map/_other/mapMarketPoints';
	import PlaceContext from '$lib/components/ui/PlaceContext.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import SpecificFeaturesSection from '$lib/components/ui/section/SpecificFeaturesSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { PrimaryLink } from '$lib/features/_other/primaryLink.type';
	import { TIME_OF_DAY_METAS, type TimeOfDayMeta } from '$lib/features/_other/timeOfDay.enum';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CITY_FEATURE_TYPE_SINGLE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import type { ItineraryDailyPlan } from '$lib/features/itinerary/dailyPlan/itineraryDailyPlan.type';
	import type { Itinerary } from '$lib/features/itinerary/itinerary.type';
	import type { ItineraryStop } from '$lib/features/itinerary/stop/itineraryStop.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { FULL_SITE_URL } from '$lib/statics';
	import { getThemeColor } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { type NavLink, UrlUtil } from '$lib/utils/url.util';
	import { type Component, onMount } from 'svelte';
	import type { Attachment } from 'svelte/attachments';
	import { fade, fly } from 'svelte/transition';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconClose from '~icons/mdi/close';
	import IconMap from '~icons/mdi/map';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.ITINERARY;
	const { data }: { data: { output: Output } } = $props();

	const sectionVisibility = $state<Record<number, boolean>>({});
	let isMobileMapOpen = $state<boolean>(false);
	let selectedDayHash = $state<string | undefined>(undefined);

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const itinerary = $derived<Itinerary>(output.item);
	const dailyPlans = $derived<ItineraryDailyPlan[]>(itinerary.dailyPlans);
	let activeDay = $derived.by<number>(() => {
		for (const plan of dailyPlans) {
			if (sectionVisibility[plan.dayNumber]) return plan.dayNumber;
		}
		return dailyPlans[0]?.dayNumber || 1;
	});
	const activeMarkers = $derived.by<MapMarkerPoint[]>(() => {
		const activePlan = dailyPlans.find((p) => p.dayNumber === activeDay) || dailyPlans[0];
		if (!activePlan) return [];
		return activePlan.stops
			.map((stop, index) => {
				const coords = stop.primary.place?.coordinates;
				if (!coords) return null;
				const { cssThemeVar } = TIME_OF_DAY_METAS[stop.timeOfDay];
				const hexColor = getThemeColor(cssThemeVar);
				return {
					coordinates: coords,
					colour: hexColor,
					label: index + 1,
					image: stop.primary.images?.[0] ? UrlUtil.getFullImageUrl(stop.primary.images[0]) : undefined
				} as MapMarkerPoint;
			})
			.filter((m): m is MapMarkerPoint => m !== null);
	});
	const meta = $derived.by(() => {
		const dayOne = dailyPlans.sort((a, b) => a.dayNumber - b.dayNumber)[0];
		const allStops = dailyPlans.flatMap((plan) => plan.stops);

		const metaImage: ImageDetails | undefined = allStops.flatMap((stop) => stop.primary.images).at(0);

		const [firstStop, ...restStops] = allStops.slice(0, 3);
		const timeLabel = firstStop.timeOfDay.charAt(0) + firstStop.timeOfDay.slice(1).toLowerCase();
		const restNames = restStops.map((stop) => stop.englishName).join(' and ');

		const title: string = `${output.item.durationDays} ${output.item.durationDays === 1 ? 'Day' : 'Days'} Itinerary for ${location.city.label}: ${allStops.length} Must-See Stops on a Map`;

		const description: string = `Day 1 — ${dayOne.themeOfDay} — ${firstStop.englishName} in the ${timeLabel.toLowerCase()}, then ${restNames}, before exploring other must-see sights.`;
		return {
			title,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeature({ location, type: featureType, featureSlug: itinerary.feature.slug }).href}`,
			imageUrl: metaImage ? UrlUtil.getFullImageUrl(metaImage) : undefined
		};
	});
	const jsonLdTouristTrip = $derived.by(() => {
		const json = data.output.jsonLdTouristTrip;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristTrip]);

	function onSelectedDay(dayNumber: number) {
		const elementId = getDailyPlanElementId(dayNumber);
		const hashedFullId = `#${elementId}`;
		selectedDayHash = hashedFullId;
		replaceState(`${hashedFullId}`, {});
		document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
	}

	function getDailyPlanElementId(dayNumber: number): string {
		return `day-${dayNumber}`;
	}

	const observeDay = (dayNumber: number): Attachment => {
		return (node: Element) => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						sectionVisibility[dayNumber] = entry.isIntersecting;
					});
				},
				{ rootMargin: '-10% 0px -10% 0px', threshold: 0 }
			);
			observer.observe(node);
			return () => {
				observer.disconnect();
				delete sectionVisibility[dayNumber];
			};
		};
	};

	function handleTabClick(dayNumber: number) {
		const el = document.getElementById(getDailyPlanElementId(dayNumber));
		if (el) {
			el.scrollIntoView({ behavior: 'smooth' });
		}
	}

	onMount(() => {
		selectedDayHash = page.url.hash ?? undefined;
	});
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />
	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={itinerary.feature.englishName}
	description={itinerary.feature.description}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: dailyPlans.flatMap((plan: ItineraryDailyPlan) => plan.stops.flatMap((s) => s.primary.place?.coordinates ?? [])),
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-8 lg:px-0">
	<PlaceContext label={CITY_FEATURE_TYPE_LABELS[featureType]} {location} className="mb-8" />

	<div class="relative flex flex-col items-start gap-8 lg:flex-row lg:gap-12">
		<div class="w-full flex-1">
			{@render DayTabs()}

			<ul class="mt-6 flex flex-col gap-16">
				{#each dailyPlans as plan (plan.dayNumber)}
					<li>
						{@render DaySection(plan)}
					</li>
				{/each}
			</ul>
		</div>

		{#if deviceScreen.biggerThanMobile}
			{@render DesktopMap()}
		{/if}
	</div>

	{#if output.otherItineraries.length > 0}
		<SpecificFeaturesSection
			{featureType}
			items={output.otherItineraries}
			{location}
			ctaText="view itinerary"
			sectionClasses="mt-8 md:mt-20"
		/>
	{/if}

	<KeepExploringSection {location} categories={output.keepExploring} />

	{#if deviceScreen.isMobile}
		{@render MobileMapButton()}
	{/if}
	{#if isMobileMapOpen}
		{@render MobileMapModal()}
	{/if}

	{#snippet DesktopMap()}
		<div class="sticky top-[10vh] hidden h-[calc(100vh-6rem)] w-full flex-1 lg:block">
			<div class="relative h-full w-full overflow-hidden rounded-box border border-base-content/5 bg-base-200 shadow-md">
				{@render MapContainer()}
			</div>
		</div>
	{/snippet}
</div>

{#snippet DayTabs()}
	<div
		class="sticky top-16 z-40 -mx-4 overflow-x-auto bg-base-100/90 px-4 py-4 shadow-sm backdrop-blur-md lg:top-0 lg:mx-0 lg:px-0 lg:shadow-none"
	>
		<ul class="flex gap-2 font-mono">
			<li>
				{#each dailyPlans as plan (plan.themeOfDay)}
					{@render ActionBtn(plan)}
				{/each}
			</li>
		</ul>
	</div>

	{#snippet ActionBtn(plan: ItineraryDailyPlan)}
		{const isActive = $derived<boolean>(activeDay === plan.dayNumber)}
		<button
			class={{
				btn: true,
				'btn-ghost btn-primary': !isActive,
				'btn-active btn-primary': isActive
			}}
			onclick={() => handleTabClick(plan.dayNumber)}
		>
			day {plan.dayNumber}
		</button>
	{/snippet}
{/snippet}

{#snippet DaySection(plan: ItineraryDailyPlan)}
	<section
		id={getDailyPlanElementId(plan.dayNumber)}
		class="flex scroll-mt-32 flex-col gap-8"
		{@attach observeDay(plan.dayNumber)}
	>
		<header class="flex flex-row gap-2">
			{@render Header()}
		</header>
		<ul class="flex flex-col gap-10 md:gap-12">
			{#each plan.stops as stop, index (stop.englishName)}
				<li class="flex gap-3 md:gap-4">
					{@render StopCard(stop, index === 0)}
				</li>
			{/each}
		</ul>
	</section>

	{#snippet Header()}
		{const elementId = $derived<string>(getDailyPlanElementId(plan.dayNumber))}
		{const isActive = $derived<boolean>(selectedDayHash === `#${elementId}`)}
		<a
			class="text-md btn btn-square font-mono btn-primary btn-sm"
			href="#{elementId}"
			data-section={plan.dayNumber}
			aria-current={isActive ? 'true' : undefined}
			onclick={(e) => {
				e.preventDefault();
				onSelectedDay(plan.dayNumber);
			}}
		>
			{plan.dayNumber}
		</a>
		<h2 class="flex items-center gap-4 text-xl lg:text-2xl">
			<span>{plan.themeOfDay}</span>
		</h2>
	{/snippet}
{/snippet}

{#snippet StopCard(stop: ItineraryStop, isFirst: boolean)}
	{const { label, Icon, textColour, bgColour } = $derived<TimeOfDayMeta>(TIME_OF_DAY_METAS[stop.timeOfDay])}
	{const primary = $derived<PrimaryLink>(stop.primary)}
	{const image = $derived<ImageDetails | undefined>(primary?.images?.[0])}
	{const featureNav = $derived<NavLink>(UrlUtil.toCityFeature({ location, type: primary.type, featureSlug: primary.slug }))}

	<div class="flex flex-col items-center pt-1.5">
		{@render TimelineLine()}
	</div>

	<div class="flex-1">
		<div class="mb-3 flex items-center gap-2 text-sm font-bold tracking-widest {textColour}">
			{@render TimeOfDayIndicator()}
		</div>

		<a
			class="group flex flex-col overflow-hidden rounded-box border border-base-content/5 bg-base-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl md:flex-row md:items-stretch"
			href={`/${featureNav.href}`}
			data-sveltekit-preload-data={featureNav.preloadData}
			data-sveltekit-preload-code={featureNav.preloadCode}
		>
			{@render Content()}
		</a>

		<PlaceExtras name={stop.englishName} place={stop.primary.place} showMap={false} />
	</div>

	{#snippet TimelineLine()}
		<div class="h-4 w-4 shrink-0 rounded-full border-3 border-base-100 {bgColour} shadow-sm outline-1 outline-base-300"></div>
		<div class="my-2 w-0.5 flex-1 rounded-full bg-base-300"></div>
	{/snippet}

	{#snippet TimeOfDayIndicator()}
		<Icon class="text-lg" />
		{label.toUpperCase()}
	{/snippet}

	{#snippet Content()}
		{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[primary.type])}
		<div class="relative h-48 w-full shrink-0 overflow-hidden bg-base-300 md:h-auto md:min-h-50 md:w-2/5">
			{@render ImageSection()}
		</div>

		<div class="flex flex-1 flex-col p-5 md:p-6">
			<div class="flex flex-1 items-center justify-center">
				<h3 class="text-lg leading-tight transition-colors group-hover:text-primary md:text-xl">
					{stop.englishName}
				</h3>
			</div>
			<div class="divider"></div>
			<div class="flex items-center justify-between">
				{@render CardBottom()}
			</div>
		</div>

		{#snippet ImageSection()}
			{#if image}
				<img
					class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
					src={UrlUtil.getFullImageUrl(image)}
					alt={image.altText}
					width={image.width}
					height={image.height}
					loading={isFirst ? 'eager' : 'lazy'}
					decoding="async"
				/>
			{:else}
				{const FallbackIcon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[primary.type])}
				<div class="absolute inset-0 flex items-center justify-center text-base-content/20">
					<FallbackIcon class="text-5xl" />
				</div>
			{/if}
		{/snippet}

		{#snippet CardBottom()}
			<span class="inline-flex gap-2 text-sm">
				<Icon class="text-base-content/70 group-hover:text-primary" />
				{CITY_FEATURE_TYPE_SINGLE_LABELS[primary.type]}
			</span>
			<span class="inline-flex items-center gap-1 font-mono text-sm font-semibold text-primary">
				read guide <IconArrowRight class="transition-transform group-hover:translate-x-1" />
			</span>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet MobileMapButton()}
	<div class="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 lg:hidden">
		<button
			class="btn rounded-full border border-secondary-content/10 px-8 shadow-2xl btn-secondary"
			onclick={() => (isMobileMapOpen = true)}
		>
			<IconMap class="mr-1 text-lg" />
			Map View
		</button>
	</div>
{/snippet}

{#snippet MobileMapModal()}
	{@render DarkBackdrop()}
	<div
		{@attach swipeClose({ onClose: () => (isMobileMapOpen = false), enabled: deviceScreen.isMobile })}
		class="fixed right-0 bottom-0 left-0 z-70 flex h-[85vh] w-full touch-none flex-col overflow-hidden rounded-t-3xl bg-base-100 shadow-2xl lg:hidden"
		role="dialog"
		aria-modal="true"
		transition:fly={{ y: 800, duration: 300, opacity: 1 }}
	>
		{@render VisualDragHandle()}

		<div class="z-10 flex items-center justify-between border-b border-base-200 bg-base-100 px-5 pb-3 shadow-sm">
			{@render Header()}
		</div>

		<div class="relative flex-1 bg-base-200">
			{@render MapContainer()}
		</div>
	</div>

	{#snippet DarkBackdrop()}
		<div
			class="fixed inset-0 z-60 bg-black/40 backdrop-blur-sm lg:hidden"
			transition:fade={{ duration: 200 }}
			onclick={() => (isMobileMapOpen = false)}
			role="button"
			tabindex="-1"
			aria-label="Close map"
			onkeydown={() => {}}
		></div>
	{/snippet}

	{#snippet VisualDragHandle()}
		<div class="flex w-full justify-center bg-base-100 pt-3 pb-1">
			<div class="h-1.5 w-12 rounded-full bg-base-300"></div>
		</div>
	{/snippet}

	{#snippet Header()}
		<div class="flex flex-col">
			<h3 class="text-lg">Route Map</h3>
			<span class="text-xs font-bold tracking-widest">DAY {activeDay}</span>
		</div>
		<button class="btn btn-circle bg-base-200 btn-ghost btn-sm" onclick={() => (isMobileMapOpen = false)}>
			<IconClose class="text-xl" />
		</button>
	{/snippet}
{/snippet}

{#snippet MapContainer()}
	{#if activeMarkers.length > 0}
		{#if browser}
			{#await import('$lib/components/ui/map/SimpleRouteMap.svelte') then { default: SimpleRouteMap }}
				<SimpleRouteMap
					markers={activeMarkers}
					padding={70}
					pitch={45}
					interactive={true}
					control={{ geo: true, navigation: true }}
				/>
			{/await}
		{:else}
			<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
				<span class="loading mr-2 loading-spinner text-primary"></span>
				<span>Loading map of {activeMarkers.length} stops...</span>
			</div>
		{/if}
	{:else}
		<div class="absolute inset-0 flex items-center justify-center">
			<span class="font-medium text-base-content/70">No locations mapped for this day.</span>
		</div>
	{/if}
{/snippet}
